import { DiffEditor } from '@monaco-editor/react'
import { Box, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { TextEditorLanguage } from '@/features/controller/configuration/types'
import { useMonacoThemes } from '@/features/controller/configuration/utils/editor/hooks/useMonacoThemes'
import { EDITOR_OPTIONS, MONACO_THEMES } from '@/features/controller/configuration/utils/editor/monacoThemes'

interface DiffViewerProps {
  modifiedText: string
  originalText: string
  language?: TextEditorLanguage
}

export function DiffViewer({
  modifiedText,
  originalText,
  language = TextEditorLanguage.ini,
}: Readonly<DiffViewerProps>) {
  const { t } = useTranslation()
  const { themesReady } = useMonacoThemes()

  const isJson = language === 'json'

  // Build consistent editor options for DiffEditor
  const editorOptions = {
    ...EDITOR_OPTIONS.base,
    ...EDITOR_OPTIONS.readonlyMode,
    links: false,
    wordWrap: 'on' as const,
    readOnly: true,
    // Related to the issue in the library where the wrap option works only on the right side
    useInlineViewWhenSpaceIsLimited: false,
    renderSideBySide: true,
    // JSON-specific formatting
    ...(isJson && {
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: true,
      formatOnType: true,
    }),
  }

  return themesReady ? (
    <DiffEditor
      height={500}
      language={language}
      modified={modifiedText}
      options={editorOptions}
      original={originalText}
      theme={MONACO_THEMES.readonlyMode.name}
      onMount={(editor) => {
        editor.revealFirstDiff()
      }}
    />
  ) : (
    <Box sx={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress title={t('controllers.configuration.loading_diff_viewer')} />
    </Box>
  )
}
